/**
 * AI Helper Module for WPS Pages
 * Provides AI-powered content generation for form fields
 */

export class AIHelper {
  constructor() {
    this.buttons = new Map();
    this.isProcessing = false;
  }

  /**
   * Initialize AI buttons for specific fields
   * @param {Array} fieldConfigs - Array of {fieldId, prompt, label} configurations
   */
  initialize(fieldConfigs = []) {
    // Default configurations for standard fields
    const defaultConfigs = [
      {
        fieldId: 'page_excerpt',
        prompt: 'Generate a brief, engaging excerpt (2-3 sentences) based on the page content',
        label: 'Generate Excerpt'
      },
      {
        fieldId: 'page_meta_title',
        prompt: 'Generate an SEO-optimized meta title (50-60 characters) based on the page content',
        label: 'Generate Meta Title'
      },
      {
        fieldId: 'page_meta_description',
        prompt: 'Generate an SEO-optimized meta description (150-160 characters) based on the page content',
        label: 'Generate Meta Description'
      }
    ];

    const configs = fieldConfigs.length > 0 ? fieldConfigs : defaultConfigs;

    configs.forEach(config => {
      this.attachButton(config);
    });
  }

  /**
   * Attach AI button to a specific field
   * @param {Object} config - {fieldId, prompt, label}
   */
  attachButton(config) {
    const field = document.getElementById(config.fieldId);
    if (!field) {
      console.warn(`AI Helper: Field not found: ${config.fieldId}`);
      return;
    }

    // Find the label for this field
    const parent = field.parentElement;
    const label = parent.querySelector('label.setting-label');

    if (!label) {
      console.warn(`AI Helper: Label not found for field: ${config.fieldId}`);
      return;
    }

    // Create button
    const button = this.createButton(config);

    // Insert button next to label text
    label.appendChild(button);

    this.buttons.set(config.fieldId, {
      button: button,
      field: field,
      prompt: config.prompt
    });
  }

  /**
   * Create AI button (icon only)
   * @param {Object} config
   * @returns {HTMLElement}
   */
  createButton(config) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ai-generate-btn inline-flex items-center justify-center w-6 h-6 ml-2 text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded';
    button.dataset.fieldId = config.fieldId;
    button.title = config.label; // Tooltip on hover

    button.innerHTML = `
      <svg class="ai-icon w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
      </svg>
      <svg class="ai-spinner hidden w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `;

    button.addEventListener('click', () => this.handleGenerate(config.fieldId));

    return button;
  }

  /**
   * Handle AI generation request
   * @param {string} fieldId
   */
  async handleGenerate(fieldId) {
    if (this.isProcessing) {
      return;
    }

    const buttonData = this.buttons.get(fieldId);
    if (!buttonData) {
      console.error('AI Helper: Button data not found for field:', fieldId);
      return;
    }

    const { button, field, prompt } = buttonData;

    try {
      this.setButtonState(button, true);
      this.isProcessing = true;

      // Get page content from AlpineBlocks editor
      const pageContent = this.extractPageContent();

      if (!pageContent || pageContent.trim().length === 0) {
        throw new Error('No page content found. Please add a page title or some content blocks to the page first.');
      }

      // Call AI endpoint
      const response = await this.callAIEndpoint(prompt, pageContent);

      if (response.success && response.content) {
        // Update field with generated content
        field.value = response.content;

        // Trigger change event for any listeners
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));

        // Show success feedback
        this.showFeedback('success', 'Content generated successfully!');
      } else {
        throw new Error(response.error || 'Failed to generate content');
      }

    } catch (error) {
      console.error('AI Helper: Generation failed:', error);
      this.showFeedback('error', error.message || 'Failed to generate content. Please try again.');
    } finally {
      this.setButtonState(button, false);
      this.isProcessing = false;
    }
  }

  /**
   * Extract page content from AlpineBlocks editor
   * @returns {string}
   */
  extractPageContent() {
    try {
      let content = '';

      // Try to get content from AlpineBlocks editor
      const editorElement = document.getElementById('alpineblocks-editor');
      if (editorElement && editorElement._x_dataStack) {
        const alpineData = editorElement._x_dataStack[0];
        if (alpineData && alpineData.blocks && Array.isArray(alpineData.blocks)) {
          const extractedText = this.extractTextFromBlocks(alpineData.blocks);
          if (extractedText && extractedText.trim().length > 0) {
            content = extractedText;
          }
        }
      }

      // If no content from blocks, try hidden form field
      if (!content || content.trim().length === 0) {
        const contentField = document.getElementById('form_content');
        if (contentField && contentField.value && contentField.value.trim().length > 0) {
          content = this.stripHTML(contentField.value);
        }
      }

      // If still no content, use page title as context
      if (!content || content.trim().length === 0) {
        const titleField = document.getElementById('page_title');
        if (titleField && titleField.value && titleField.value.trim().length > 0) {
          content = titleField.value;
        }
      }

      // If still no content, collect any visible text in the editor preview
      if (!content || content.trim().length === 0) {
        const previewArea = document.querySelector('#alpineblocks-editor .editor-preview, #alpineblocks-editor [data-blocks-container]');
        if (previewArea) {
          const visibleText = previewArea.innerText || previewArea.textContent;
          if (visibleText && visibleText.trim().length > 0) {
            content = visibleText.trim();
          }
        }
      }

      return content.trim();
    } catch (error) {
      console.error('AI Helper: Failed to extract content:', error);
      return '';
    }
  }

  /**
   * Extract text content from AlpineBlocks data structure
   * @param {Array} blocks
   * @returns {string}
   */
  extractTextFromBlocks(blocks) {
    if (!Array.isArray(blocks)) return '';

    let text = [];

    blocks.forEach(block => {
      if (block.data) {
        // Extract content based on block type
        if (block.data.content) {
          text.push(this.stripHTML(block.data.content));
        }
        if (block.data.text) {
          text.push(this.stripHTML(block.data.text));
        }
        if (block.data.heading) {
          text.push(this.stripHTML(block.data.heading));
        }
        if (block.data.title) {
          text.push(this.stripHTML(block.data.title));
        }
      }

      // Recursively extract from nested blocks
      if (block.children && Array.isArray(block.children)) {
        text.push(this.extractTextFromBlocks(block.children));
      }
    });

    return text.filter(t => t.trim().length > 0).join(' ');
  }

  /**
   * Strip HTML tags from content
   * @param {string} html
   * @returns {string}
   */
  stripHTML(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Call AI endpoint
   * @param {string} prompt
   * @param {string} content
   * @returns {Promise<Object>}
   */
  async callAIEndpoint(prompt, content) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const response = await fetch('/admin/pages/ai/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        prompt: prompt,
        content: content
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Set button loading state
   * @param {HTMLElement} button
   * @param {boolean} isLoading
   */
  setButtonState(button, isLoading) {
    const icon = button.querySelector('.ai-icon');
    const spinner = button.querySelector('.ai-spinner');

    if (isLoading) {
      button.disabled = true;
      icon.classList.add('hidden');
      spinner.classList.remove('hidden');
    } else {
      button.disabled = false;
      icon.classList.remove('hidden');
      spinner.classList.add('hidden');
    }
  }

  /**
   * Show feedback message
   * @param {string} type - 'success' or 'error'
   * @param {string} message
   */
  showFeedback(type, message) {
    // Look for existing Rails flash container
    const flashContainer = document.getElementById('flash-messages') || document.querySelector('.flash');

    if (flashContainer) {
      const alert = document.createElement('div');
      alert.className = `alert alert-${type === 'success' ? 'success' : 'danger'} mb-4 p-4 rounded-md ${
        type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
      }`;
      alert.textContent = message;

      flashContainer.appendChild(alert);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        alert.remove();
      }, 5000);
    } else {
      // Fallback to simple alert
      if (type === 'success') {
        console.log('✓ ' + message);
      } else {
        alert(message);
      }
    }
  }

  /**
   * Remove all AI buttons
   */
  destroy() {
    this.buttons.forEach((data, fieldId) => {
      if (data.button && data.button.parentElement) {
        data.button.remove();
      }
    });
    this.buttons.clear();
  }
}

// Export for global access
window.AIHelper = AIHelper;
